import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import { metricOptimizationsAPI, metricAnnotationsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import { usePageTitle } from "../contexts/PageTitleContext";
import { useToast } from "../contexts/ToastContext";
import { usePostHog } from "@posthog/react";
import {
  ArrowLeft,
  Sparkles,
  Save,
  Play,
  ChevronDown,
  ChevronUp,
  Trash2,
  Eye,
  Pencil,
  ExternalLink,
  Plus,
  CheckCircle2,
} from "lucide-react";

const AVAILABLE_VARIABLES = [
  { token: "{provided_transcript}", label: "Provided Transcript", always: true },
  { token: "{generated_transcript}", label: "Generated Transcript", always: false, hint: "Voice calls only" },
  { token: "{call_duration}", label: "Call Duration", always: true },
  { token: "{call_metadata}", label: "Call Metadata", always: true },
  { token: "{call_direction}", label: "Call Direction", always: true },
  { token: "{agent_name}", label: "Agent Name", always: true },
];

const extractUsedVariables = (text) => {
  if (!text) return [];
  const matches = text.match(/\{[^}]+\}/g);
  return matches ? [...new Set(matches)] : [];
};

const MetricOptimization = () => {
  const { metricId } = useParams();
  const navigate = useWsNavigate();
  const { currentWorkspace } = useAuth();
  const { selectedAgent } = useAgent();
  const { setPageTitle } = usePageTitle();
  const { showToast } = useToast();
  const posthog = usePostHog();
  const textareaRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [instructions, setInstructions] = useState("");
  const [instructionsChanged, setInstructionsChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedAnnotations, setSelectedAnnotations] = useState([]);
  const [expandedTranscripts, setExpandedTranscripts] = useState({});
  const [expandedExplanations, setExpandedExplanations] = useState({});
  const [editingExpected, setEditingExpected] = useState(null);
  const [editExpectedValue, setEditExpectedValue] = useState("");
  const [resolvingId, setResolvingId] = useState(null);
  const [resolveNote, setResolveNote] = useState("");

  useEffect(() => {
    setPageTitle("Metric Optimization");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  const fetchData = useCallback(async () => {
    if (!currentWorkspace?.id || !metricId) return;
    try {
      setLoading(true);
      const result = await metricOptimizationsAPI.show(
        currentWorkspace.id,
        metricId,
        selectedAgent?.id
      );
      setData(result);
      setInstructions(result.metric?.llm_instructions || "");
      setInstructionsChanged(false);
    } catch (err) {
      console.error("Failed to load optimization data:", err);
      showToast("Failed to load metric optimization data", "error");
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, metricId, selectedAgent, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const insertVariable = (token) => {
    setInstructions((prev) => prev + " " + token);
    setInstructionsChanged(true);
  };

  const handleSaveInstructions = async () => {
    setSaving(true);
    try {
      await metricOptimizationsAPI.updateInstructions(
        currentWorkspace.id,
        metricId,
        instructions,
        "Manual edit from optimization page"
      );
      posthog?.capture('metric_optimization_saved', { metric_id: metricId });
      showToast("Instructions updated", "success");
      setInstructionsChanged(false);
      fetchData();
    } catch (err) {
      showToast("Failed to save instructions", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      const agentId = selectedAgent?.id;
      let result;
      switch (action) {
        case "reevaluate":
          result = await metricOptimizationsAPI.reevaluate(currentWorkspace.id, metricId, agentId);
          break;
        case "auto_improve":
          result = await metricOptimizationsAPI.autoImprove(currentWorkspace.id, metricId, agentId);
          break;
        case "dspy_train":
          result = await metricOptimizationsAPI.dspyTrain(currentWorkspace.id, metricId, agentId);
          break;
        default:
          return;
      }
      showToast(result.message || "Action queued", "success");
      setTimeout(fetchData, 2000);
    } catch (err) {
      const msg = err.response?.data?.error || "Action failed";
      showToast(msg, "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAnnotation = async (annotationId) => {
    if (!window.confirm("Remove this annotation?")) return;
    try {
      await metricAnnotationsAPI.destroy(currentWorkspace.id, annotationId);
      showToast("Annotation removed", "success");
      fetchData();
    } catch (err) {
      showToast("Failed to remove annotation", "error");
    }
  };

  const handleResolveAnnotation = async (annotationId) => {
    try {
      await metricAnnotationsAPI.resolve(currentWorkspace.id, annotationId, resolveNote);
      showToast("Annotation resolved", "success");
      setResolvingId(null);
      setResolveNote("");
      fetchData();
    } catch (err) {
      showToast("Failed to resolve annotation", "error");
    }
  };

  const toggleSelectAll = () => {
    if (!data) return;
    if (selectedAnnotations.length === data.annotations.length) {
      setSelectedAnnotations([]);
    } else {
      setSelectedAnnotations(data.annotations.map((a) => a.id));
    }
  };

  const toggleSelectAnnotation = (id) => {
    setSelectedAnnotations((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!data) {
    return <div className="flex justify-center items-center h-64 text-muted-foreground">Failed to load data</div>;
  }

  const { metric, annotations, instruction_versions } = data;
  const optimizationType = metric.optimization_type || "llm"; // "llm" | "code" | "audio"
  const isLlmMetric = optimizationType === "llm";
  const isCodeMetric = optimizationType === "code";
  const isAudioMetric = optimizationType === "audio";
  const isSuperAdmin = (JSON.parse(localStorage.getItem("userData") || "{}"))?.roles?.includes("super_admin");
  const pendingCount = annotations.filter((a) => a.status === "pending").length;
  const resolvedCount = annotations.filter((a) => a.status === "resolved").length;
  const usedVariables = extractUsedVariables(instructions);
  const triggerLabel = metric.evaluation_trigger?.type === "call_state"
    ? metric.evaluation_trigger?.state_prompt || "Call State"
    : metric.evaluation_trigger?.type === "custom"
    ? "Custom"
    : "Always";

  const formatValue = (val) => {
    if (val === null || val === undefined) return "—";
    const v = val?.value ?? val;
    if (typeof v === "boolean") return v ? "Pass" : "Fail";
    if (typeof v === "number") return `${v}%`;
    return String(v);
  };

  const getValuePercent = (val, resultType) => {
    const v = val?.value ?? val;
    if (resultType === "boolean") return v ? 100 : 0;
    if (resultType === "rating") return (Number(v) / 5) * 100;
    if (typeof v === "number") return Math.min(v, 100);
    return 0;
  };

  const getValueBarColor = (val, expected, resultType) => {
    const vNum = val?.value ?? val;
    const eNum = expected?.value ?? expected;
    if (resultType === "boolean") {
      return vNum === eNum ? "bg-green-500" : "bg-red-400";
    }
    return vNum === eNum ? "bg-green-500" : "bg-orange-400";
  };

  return (
    <div className="px-4">
      {/* ══════ Header ══════ */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/evaluation/metrics")}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-foreground">{metric.name}</h1>
            <span className="text-xs text-muted-foreground capitalize">
              {metric.result_type === "boolean" ? "Pass/Fail" : metric.result_type}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isLlmMetric && (
            <>
              <button
                onClick={() => handleAction("auto_improve")}
                disabled={actionLoading !== null || pendingCount === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className={`h-4 w-4 ${actionLoading === "auto_improve" ? "animate-spin" : ""}`} />
                Auto Improve
              </button>
              <button
                onClick={handleSaveInstructions}
                disabled={saving || !instructionsChanged}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground bg-card border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => handleAction("reevaluate")}
                disabled={actionLoading !== null || pendingCount === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className={`h-4 w-4 ${actionLoading === "reevaluate" ? "animate-spin" : ""}`} />
                Run
              </button>
            </>
          )}
          {isCodeMetric && (
            <button
              onClick={() => navigate(`/evaluation/custom-metrics?edit=${metricId}`)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800"
            >
              <Pencil className="h-4 w-4" />
              Edit Metric Definition
            </button>
          )}
          {isAudioMetric && (
            <span className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
              Audio metrics require manual review
            </span>
          )}
        </div>
      </div>

      {/* ══════ Non-LLM info banners ══════ */}
      {isCodeMetric && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <p className="text-sm text-blue-800">
            This is a <strong>code-as-judge</strong> metric evaluated by Python code. To fix incorrect results, edit the metric definition or the evaluation code. LLM prompt optimization does not apply.
          </p>
        </div>
      )}
      {isAudioMetric && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <p className="text-sm text-amber-800">
            This is an <strong>audio analysis</strong> metric. Results are computed from audio processing, not LLM prompts. Annotations below are for review only.
            {isSuperAdmin ? " As an admin, you can resolve annotations after reviewing and fixing the underlying issue." : " An admin will review and resolve these."}
          </p>
        </div>
      )}

      {/* ══════ LLM Optimization Section ══════ */}
      {isLlmMetric && (
      <>
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
        <span className="text-xs text-muted-foreground shrink-0">Available Variables</span>
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        {AVAILABLE_VARIABLES.map((v) => (
          <button
            key={v.token}
            type="button"
            onClick={() => insertVariable(v.token)}
            className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              v.always
                ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
            }`}
            title={v.hint || v.label}
          >
            {v.token}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <textarea
          ref={textareaRef}
          value={instructions}
          onChange={(e) => {
            setInstructions(e.target.value);
            setInstructionsChanged(e.target.value !== (metric.llm_instructions || ""));
          }}
          className="w-full px-4 py-3 border border-input rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-muted/50"
          rows={8}
          placeholder="Metric LLM instructions..."
        />
      </div>

      {/* ══════ Variables Used ══════ */}
      {usedVariables.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs font-medium text-foreground">Variables used:</span>
          {usedVariables.map((v) => (
            <span
              key={v}
              className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
            >
              {v}
            </span>
          ))}
        </div>
      )}

      {/* ══════ Evaluation Trigger ══════ */}
      <div className="mb-6">
        <p className="text-xs font-medium text-foreground mb-1">Evaluation Trigger</p>
        <p className="text-xs text-muted-foreground mb-1.5">Choose when this metric should be evaluated</p>
        <div className="inline-block px-3 py-2 border border-input rounded-lg text-sm bg-background text-foreground">
          {triggerLabel}
        </div>
      </div>
      </>
      )}

      {/* ══════ Score Summary + Annotations Table ══════ */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {annotations.length >= 3 && (
              <button
                onClick={() => handleAction("dspy_train")}
                disabled={actionLoading !== null}
                className="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
              >
                DSPy Optimize ({annotations.length} examples)
              </button>
            )}
          </div>
          <span className="text-sm font-semibold text-foreground">
            Overall Score : {resolvedCount}/{annotations.length}
          </span>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {annotations.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-muted-foreground">
              No annotations yet. Mark metric results as incorrect from the call detail or run results page.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-3 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selectedAnnotations.length === annotations.length && annotations.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-input"
                    />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">#</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Source</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Expected</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actual</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Explanation</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {annotations.map((a, idx) => {
                  const expectedVal = formatValue(a.expected_value);
                  const actualVal = formatValue(a.measured_value);
                  const expectedPercent = getValuePercent(a.expected_value, metric.result_type);
                  const actualPercent = getValuePercent(a.measured_value, metric.result_type);
                  const barColor = getValueBarColor(a.measured_value, a.expected_value, metric.result_type);
                  const isExpanded = expandedTranscripts[a.id];
                  const isExplanationExpanded = expandedExplanations[a.id];
                  const annotatable = a.annotatable_type === "CallObservation" ? "Call" : "Run";

                  return (
                    <tr key={a.id} className="align-top hover:bg-muted/50">
                      {/* Checkbox */}
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedAnnotations.includes(a.id)}
                          onChange={() => toggleSelectAnnotation(a.id)}
                          className="rounded border-input"
                        />
                      </td>

                      {/* ID */}
                      <td className="px-3 py-3 text-foreground font-medium">{idx + 1}</td>

                      {/* Source — Call or Run link */}
                      <td className="px-3 py-3">
                        <button
                          onClick={() => {
                            const path = a.annotatable_type === "CallObservation"
                              ? `/observability/conversations/${a.annotatable_id}`
                              : `/batch-run/${a.annotatable_id}`;
                            navigate(path);
                          }}
                          className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          {annotatable} #{a.annotatable_id}
                          <ExternalLink className="h-3 w-3" />
                        </button>
                        {a.user && (
                          <div className="text-xs text-muted-foreground mt-0.5">by {a.user.name}</div>
                        )}
                        {a.transcript_preview && (
                          <>
                            <p className={`text-xs text-muted-foreground mt-1 ${!isExpanded ? "line-clamp-2" : ""}`}>
                              {a.transcript_preview}
                            </p>
                            {a.transcript_preview?.length > 100 && (
                              <button
                                onClick={() => setExpandedTranscripts((prev) => ({ ...prev, [a.id]: !prev[a.id] }))}
                                className="text-xs text-indigo-600 hover:text-indigo-700 mt-0.5"
                              >
                                {isExpanded ? "Show less" : "Read More"}
                              </button>
                            )}
                          </>
                        )}
                        <button
                          onClick={() => {
                            const path = a.annotatable_type === "CallObservation"
                              ? `/observability/calls/${a.annotatable_id}`
                              : `/batch-run/${a.annotatable_id}`;
                            navigate(path);
                          }}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-1.5"
                        >
                          <Eye className="h-3 w-3" /> See Variables
                        </button>
                      </td>

                      {/* Expected Value */}
                      <td className="px-3 py-3 w-32">
                        <div className="w-full bg-muted rounded-full h-2.5 mb-1">
                          <div
                            className="bg-orange-400 h-2.5 rounded-full transition-all"
                            style={{ width: `${expectedPercent}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-foreground">{expectedVal}</span>
                          {editingExpected === a.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={editExpectedValue}
                                onChange={(e) => setEditExpectedValue(e.target.value)}
                                className="w-16 px-1 py-0.5 text-xs border border-input bg-background rounded"
                                autoFocus
                              />
                              <button
                                onClick={() => {
                                  // TODO: save updated expected value
                                  setEditingExpected(null);
                                }}
                                className="text-xs text-indigo-600"
                              >
                                OK
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingExpected(a.id);
                                setEditExpectedValue(String(a.expected_value?.value ?? a.expected_value ?? ""));
                              }}
                              className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="h-2.5 w-2.5" /> Edit
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Actual Value */}
                      <td className="px-3 py-3 w-32">
                        <div className="w-full bg-muted rounded-full h-2.5 mb-1">
                          <div
                            className={`${barColor} h-2.5 rounded-full transition-all`}
                            style={{ width: `${actualPercent}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-foreground">{actualVal}</span>
                      </td>

                      {/* Explanation */}
                      <td className="px-3 py-3 max-w-[200px]">
                        {a.explanation ? (
                          <>
                            <p className={`text-xs text-muted-foreground ${!isExplanationExpanded ? "line-clamp-4" : ""}`}>
                              {a.explanation}
                            </p>
                            {a.explanation?.length > 120 && (
                              <button
                                onClick={() => setExpandedExplanations((prev) => ({ ...prev, [a.id]: !prev[a.id] }))}
                                className="text-xs text-indigo-600 hover:text-indigo-700 mt-0.5"
                              >
                                {isExplanationExpanded ? "Show less" : "Read more"}
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          a.status === "resolved" ? "bg-green-100 text-green-700" :
                          a.status === "unresolved" ? "bg-red-100 text-red-700" :
                          "bg-yellow-100 text-yellow-700"
                        }`}>
                          {a.status}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {a.status === "resolved" ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600">
                              <CheckCircle2 className="h-3 w-3" /> Resolved
                              {a.reevaluation_data?.resolution_note && (
                                <span className="text-muted-foreground" title={a.reevaluation_data.resolution_note}>— {a.reevaluation_data.resolution_note.slice(0, 30)}{a.reevaluation_data.resolution_note.length > 30 ? "..." : ""}</span>
                              )}
                            </span>
                          ) : (
                            <>
                              {/* Resolve button — for audio/code metrics, admin only */}
                              {(isAudioMetric || isCodeMetric) && isSuperAdmin && (
                                resolvingId === a.id ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      value={resolveNote}
                                      onChange={(e) => setResolveNote(e.target.value)}
                                      placeholder="Resolution note..."
                                      className="text-xs border border-input rounded px-2 py-1 w-36"
                                      autoFocus
                                      onKeyDown={(e) => e.key === "Enter" && handleResolveAnnotation(a.id)}
                                    />
                                    <button onClick={() => handleResolveAnnotation(a.id)} className="text-xs text-green-600 hover:text-green-800 font-medium">Save</button>
                                    <button onClick={() => { setResolvingId(null); setResolveNote(""); }} className="text-xs text-muted-foreground hover:text-muted-foreground">Cancel</button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => { setResolvingId(a.id); setResolveNote(""); }}
                                    className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800"
                                    title="Mark as resolved"
                                  >
                                    <CheckCircle2 className="h-3 w-3" /> Resolve
                                  </button>
                                )
                              )}
                              <button
                                onClick={() => handleDeleteAnnotation(a.id)}
                                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500"
                                title="Remove annotation"
                              >
                                <Trash2 className="h-3 w-3" /> Remove
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ══════ Instruction Version History ══════ */}
      <div className="bg-card border border-border rounded-lg mb-8">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full px-5 py-3 flex items-center justify-between text-sm font-semibold text-foreground hover:bg-muted/50"
        >
          <span>Instruction History ({instruction_versions.length})</span>
          {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showHistory && instruction_versions.length > 0 && (
          <div className="divide-y divide-gray-100 border-t border-border">
            {instruction_versions.map((v) => (
              <div key={v.id} className="px-5 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                    v.change_type === "auto_improve"
                      ? "bg-purple-50 text-purple-700 border-purple-200"
                      : v.change_type === "dspy_optimized"
                      ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                      : "bg-muted/50 text-muted-foreground border-border"
                  }`}>
                    {v.change_type.replace(/_/g, " ")}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(v.created_at).toLocaleString()}
                  </span>
                  {v.user && (
                    <span className="text-[10px] text-muted-foreground">
                      by {v.user.email?.split("@")[0]}
                    </span>
                  )}
                </div>
                {v.change_summary && (
                  <p className="text-xs text-muted-foreground">{v.change_summary}</p>
                )}
              </div>
            ))}
          </div>
        )}
        {showHistory && instruction_versions.length === 0 && (
          <div className="px-5 py-4 text-center text-xs text-muted-foreground border-t border-border">
            No instruction changes yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricOptimization;
